import { faEllipsis, faPencil, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CSSProperties, KeyboardEvent, useCallback, useEffect, useId, useRef, useState } from "react";
import useDismiss from "app/modules/aiAssistant/page/hooks/useDismiss";
import "app/modules/aiAssistant/styles/shared.scss";
import "./ChatOptionsMenu.scss";

type Props = {
  chatSessionID: string;
  onRename: () => void;
  onDelete: () => void;
};

const MENU_GAP = 4;
// Tall enough for the delete confirmation, so it doesn't run off the bottom of the screen
const MENU_HEIGHT_ESTIMATE = 150;

const ChatOptionsMenu = ({ chatSessionID, onRename, onDelete }: Props) => {
  const [menuStyle, setMenuStyle] = useState<CSSProperties>();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const cancelDeleteRef = useRef<HTMLButtonElement>(null);
  const menuID = useId();
  const confirmTextID = useId();
  const isOpen = menuStyle != undefined;

  const resetMenu = useCallback(() => {
    setMenuStyle(undefined);
    setConfirmingDelete(false);
  }, []);

  const closeMenu = (returnFocus = false) => {
    resetMenu();
    if (returnFocus) buttonRef.current?.focus();
  };

  // Fixed position so the chat list's scroll area can't clip the menu
  const openMenu = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;

    const right = window.innerWidth - rect.right;
    const fitsBelow = rect.bottom + MENU_GAP + MENU_HEIGHT_ESTIMATE <= window.innerHeight;
    setMenuStyle(
      fitsBelow ? { top: rect.bottom + MENU_GAP, right } : { bottom: window.innerHeight - rect.top + MENU_GAP, right }
    );
  };

  useDismiss([buttonRef, menuRef], isOpen, closeMenu);

  useEffect(() => {
    if (!isOpen) return;

    menuRef.current?.querySelector<HTMLButtonElement>("button")?.focus();

    // Only scrolling that moves the button matters, not e.g. the chat scrolling to a new reply
    const closeOnScroll = (e: Event) => {
      if (e.target instanceof Node && buttonRef.current && e.target.contains(buttonRef.current)) {
        resetMenu();
      }
    };

    window.addEventListener("scroll", closeOnScroll, true);
    window.addEventListener("resize", resetMenu);
    return () => {
      window.removeEventListener("scroll", closeOnScroll, true);
      window.removeEventListener("resize", resetMenu);
    };
  }, [isOpen, resetMenu]);

  // Cancel is the safe default if someone presses Enter straight away
  useEffect(() => {
    if (confirmingDelete) cancelDeleteRef.current?.focus();
  }, [confirmingDelete]);

  const handleMenuKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>("button") ?? []);
    const currentIndex = items.indexOf(document.activeElement as HTMLButtonElement);
    const focusNext = () => items[(currentIndex + 1) % items.length]?.focus();
    const focusPrevious = () => items[(currentIndex - 1 + items.length) % items.length]?.focus();

    if (e.key === "Tab") {
      e.preventDefault();
      // The confirmation keeps focus so Tab can reach its Delete button
      if (!confirmingDelete) {
        closeMenu(true);
      } else if (e.shiftKey) {
        focusPrevious();
      } else {
        focusNext();
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      focusNext();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      focusPrevious();
    }
  };

  const choose = (action: () => void) => {
    closeMenu();
    action();
  };

  return (
    <>
      <button
        ref={buttonRef}
        data-test-id={`assistimo-chats-list__options-button-${chatSessionID}`}
        type="button"
        className="assistimo-icon-button assistimo-chat-list-item__options-button"
        aria-label="Chat options"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={isOpen ? menuID : undefined}
        onClick={() => (isOpen ? closeMenu() : openMenu())}
      >
        <FontAwesomeIcon icon={faEllipsis} />
      </button>
      {isOpen && (
        <div
          ref={menuRef}
          id={menuID}
          role={confirmingDelete ? "alertdialog" : "menu"}
          aria-label={confirmingDelete ? "Delete this chat?" : "Chat options"}
          aria-describedby={confirmingDelete ? confirmTextID : undefined}
          className="assistimo-menu"
          style={menuStyle}
          onKeyDown={handleMenuKeyDown}
        >
          {confirmingDelete ? (
            <div className="assistimo-delete-confirm">
              <div className="assistimo-delete-confirm__title">Delete this chat?</div>
              <div id={confirmTextID} className="assistimo-delete-confirm__text">
                This can&apos;t be undone.
              </div>
              <div className="assistimo-delete-confirm__actions">
                <button
                  ref={cancelDeleteRef}
                  data-test-id={`assistimo-chats-list__cancel-delete-button-${chatSessionID}`}
                  type="button"
                  className="assistimo-button assistimo-button--small"
                  onClick={() => closeMenu(true)}
                >
                  Cancel
                </button>
                <button
                  data-test-id={`assistimo-chats-list__confirm-delete-button-${chatSessionID}`}
                  type="button"
                  className="assistimo-button assistimo-button--small assistimo-button--danger"
                  onClick={() => choose(onDelete)}
                >
                  Delete
                </button>
              </div>
            </div>
          ) : (
            <>
              <button
                data-test-id={`assistimo-chats-list__rename-button-${chatSessionID}`}
                type="button"
                role="menuitem"
                className="assistimo-menu__item"
                onClick={() => choose(onRename)}
              >
                <FontAwesomeIcon icon={faPencil} fixedWidth />
                Rename
              </button>
              <div role="separator" className="assistimo-menu__divider" />
              <button
                data-test-id={`assistimo-chats-list__delete-button-${chatSessionID}`}
                type="button"
                role="menuitem"
                className="assistimo-menu__item assistimo-menu__item--danger"
                onClick={() => setConfirmingDelete(true)}
              >
                <FontAwesomeIcon icon={faTrash} fixedWidth />
                Delete
              </button>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default ChatOptionsMenu;
